import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import api from "../../config/URL";
import Delete from "../../components/common/Delete";
import { IoMdDownload } from "react-icons/io";
import { toast } from "react-toastify";
import { useFormik } from "formik";

function DocumentView() {
  const { id } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialApproveStatus = queryParams.get("approveStatus") === "true";
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [documentData, setDocumentData] = useState("");
  const [loadIndicator, setLoadIndicator] = useState(false);
  const [approveStatus, setApproveStatus] = useState(initialApproveStatus);
  const [showUploadInput, setShowUploadInput] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);

  /* 🔹 PAGINATION STATES (NEW) */
  const [page, setPage] = useState(0);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);

  const fileInputRef = useRef(null);
  const storedScreens = JSON.parse(localStorage.getItem("screens") || "{}");
  const tableBottomRef = useRef(null);

  /* ================= FETCH DATA (UPDATED ONLY) ================= */
  const fetchData = async () => {
    try {
      const response = await api.get(
        `getAllDocumentFilesByDocumentId/${id}?page=${page}&size=${pageSize}`
      );

      setData(response.data.data); // ✅ response-based
      setTotalPages(response.data.totalPages); // ✅ pagination meta
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id, page]); // ✅ page added only

  /* ================= FETCH FOLDER (UNCHANGED) ================= */
  useEffect(() => {
    const fetchDocument = async () => {
      try {
        const response = await api.get(`/getAllDocumentFolderById/${id}`);
        setDocumentData(response.data);
      } catch (error) {
        console.error("Error fetching folder name:", error);
      }
    };
    fetchDocument();
  }, [id]);

  /* ================= REFRESH (UPDATED ONLY) ================= */
  const refreshData = async () => {
    fetchData();
  };

  /* ================= DOWNLOAD (UNCHANGED) ================= */
  const downloadFiles = async () => {
    setLoadIndicator(true);
    const zip = new JSZip();
    for (const img of data) {
      const response = await fetch(img.fileAttachment);
      const blob = await response.blob();
      zip.file(img.fileAttachment.split("/").pop(), blob);
    }
    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, documentData.folderName);
    setLoadIndicator(false);
  };

  /* ================= APPROVE (UNCHANGED) ================= */
  const approveUser = async () => {
    try {
      const response = await api.put(`/approveUser/${id}`, {
        headers: { "Content-Type": "application/json" },
      });
      if (response.status === 200) {
        toast.success(response.data.message);
        setApproveStatus(false);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.message || "Can't be Cancelled");
    }
  };

  /* ================= UPLOAD (UNCHANGED) ================= */
  const formik = useFormik({
    initialValues: {
      centerName: documentData.centerId || "",
      course: documentData.courseId || "",
      classId: documentData.classId || "",
      classListing: documentData.centerId || "",
      folder: id || "",
      files: [],
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      setLoadIndicator(true);
      try {
        const formData = new FormData();
        selectedFiles.forEach((file) => {
          formData.append("files", file);
        });
        formData.append("documentId", id);
        formData.append("centerName", values.centerName);
        formData.append("course", values.course);
        formData.append("classId", values.classId);
        formData.append("classListing", values.classListing);
        formData.append("folder", values.folder);

        const response = await api.post(
          `/uploadStudentFilesByDocumentId`,
          formData,
          { headers: { "content-type": "multipart/form-data" } }
        );

        if (response.status === 200) {
          toast.success(response.data.message);
          fetchData(); // ✅ reload current page
          formik.resetForm();
          setSelectedFiles([]);
          setShowUploadInput(false);
          tableBottomRef.current?.scrollIntoView({ behavior: "smooth" });
        } else {
          toast.error(response.data.message);
        }
      } catch (error) {
        toast.error("Error uploading files");
      } finally {
        setLoadIndicator(false);
      }
    },
  });

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(files);
    formik.setFieldValue("files", files);
  };

  const handleUploadClick = () => {
    setShowUploadInput(true);
    setTimeout(() => fileInputRef.current?.click(), 0);
  };

  return (
    <div className="container">
      <ol
        className="breadcrumb my-3"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          Folder Management
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          <Link to="/document" className="custom-breadcrumb">
            Folder
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="breadcrumb-item active" aria-current="page">
          Folder View
        </li>
      </ol>

      <div className="card">
        <div
          className="d-flex flex-column flex-md-row px-4 justify-content-between align-items-start align-items-md-center p-1 gap-2"
          style={{ background: "#f5f7f9" }}
        >
          <div className="d-flex align-items-center">
            <div className="dot active me-2"></div>
            <span className="text-muted">View Folder</span>
          </div>

          <div className="d-flex flex-wrap justify-content-start justify-content-md-end align-items-center gap-2 p-2">
            {approveStatus && (
              <button
                type="button"
                onClick={approveUser}
                className="btn btn-sm btn-border"
              >
                Approve User
              </button>
            )}

            <button
              type="button"
              className="btn btn-sm text-white"
              style={{ background: "#eb862a" }}
              onClick={handleUploadClick}
            >
              Upload Files
            </button>

            {selectedFiles.length > 0 && (
              <button
                type="button"
                className="btn btn-sm btn-button"
                onClick={formik.handleSubmit}
                disabled={loadIndicator}
              >
                {loadIndicator ? "Saving..." : "Save"}
              </button>
            )}

            <Link to="/document">
              <button type="button" className="btn btn-sm btn-border">
                Back
              </button>
            </Link>
          </div>
        </div>
        {/* TABLE */}
        <div className="container">
          <div className="my-2 d-flex justify-content-between">
            <div className="d-none">
              {showUploadInput && (
                <div className="mb-3">
                  <input
                    type="file"
                    ref={fileInputRef}
                    multiple
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                  />
                  {selectedFiles.length > 0 && (
                    <div>
                      <p>Selected files:</p>
                      <ul>
                        {selectedFiles.map((file, index) => (
                          <li key={index}>{file.name}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Files</th>
                  <th>File Type</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {data && data.length > 0 ? (
                  data.map((item, index) => (
                    <tr key={item.id}>
                      <th>{page * pageSize + index + 1}</th>
                      <td>
                        {item.fileExtension.toLowerCase() === "mp4" ? (
                          <video
                            width="300"
                            height="200"
                            controls
                            style={{ objectFit: "cover", borderRadius: "8px" }}
                          >
                            <source
                              src={item.fileAttachment}
                              type="video/mp4"
                            />
                            Your browser does not support the video tag.
                          </video>
                        ) : (
                          <img
                            src={item.fileAttachment}
                            width="300"
                            alt="document"
                            style={{ borderRadius: "8px" }}
                          />
                        )}
                      </td>
                      <td>{item.fileExtension}</td>
                      <td>
                        {storedScreens?.documentListingDelete && (
                          <Delete
                            onSuccess={refreshData}
                            path={`/deleteDocumentFiles/${item.id}`}
                          />
                        )}
                        <button
                          className="btn"
                          onClick={() =>
                            window.open(item.fileAttachment, "_blank")
                          }
                        >
                          <IoMdDownload />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center">
                      No Records Found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <div ref={tableBottomRef}></div>
          </div>

          {/* 🔹 PAGINATION UI (ONLY NEW JSX) */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-end my-2">
              <ul className="pagination mb-0">
                <li className={`page-item ${page === 0 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Previous
                  </button>
                </li>

                {[...Array(totalPages)].map((_, i) => (
                  <li
                    key={i}
                    className={`page-item ${page === i ? "active" : ""}`}
                  >
                    <button className="page-link" onClick={() => setPage(i)}>
                      {i + 1}
                    </button>
                  </li>
                ))}

                <li
                  className={`page-item ${
                    page === totalPages - 1 ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DocumentView;
